import React, { useCallback, useEffect } from 'react'
import { Link } from 'react-router-dom'
import useEmblaCarousel from 'embla-carousel-react'

// Components
import CardLoading from '../../components/component-items/loading-popover'
import { useFetch } from '../../components/component-items/helpers'
import { CarouselItemStyled, ProjectStyled } from './styles'

// Fontawesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronLeft, faChevronRight, faBoxOpen } from '@fortawesome/pro-regular-svg-icons'

import { CarouselItemProps } from './types'

const CarouselItem = ({
  title,
  dateCreated,
  previewUrl,
  detailUrl,
  bomVariant,
  bomVariantId,
  explode,
  quantity,
}: CarouselItemProps) => {
  return (
    <CarouselItemStyled>
      <div className="body">
        <div className="widget__product">
          <div className="thumb">
            <Link to={detailUrl}>
              <i>
                <FontAwesomeIcon icon={faBoxOpen} />
              </i>
            </Link>
          </div>
          <div className="widget__content">
            <Link to={detailUrl}>
              <h3>{title}</h3>
            </Link>
            <div className="desc">
              Product Variant:{' '}
              <strong>
                <Link to={`/product/variant/${bomVariantId}/`}>{bomVariant}</Link>
              </strong>
            </div>
            <div>
              {explode ? 'Exploding' : 'Building'}: {quantity}
            </div>
          </div>
        </div>
      </div>
      <div className="footer">
        <div className="footer__info">
          <div className="label">Created {dateCreated}</div>
        </div>
        <div className="footer__toolbar">
          <Link to={previewUrl}>Preview</Link>
          <Link to={detailUrl}>Details</Link>
        </div>
      </div>
    </CarouselItemStyled>
  )
}

export const SlidingInfoCard = ({ url }: { url: string }) => {
  const resp: any = useFetch(url, {})
  const projects = resp.loaded ? resp?.response?.projects : []
  const total = projects?.length ?? 0

  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: 'start', containScroll: 'trimSnaps' })

  // Recompute carousel when data load state or carousel API changes
  useEffect(() => {
    if (emblaApi && resp.loaded) {
      emblaApi.reInit()
    }
  }, [emblaApi, resp.loaded])

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi])
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi])

  return (
    <ProjectStyled>
      <div className="head">
        <div className="label">Projects - {total}</div>
        <div className="nav">
          <button onClick={scrollPrev}>
            <i>
              <FontAwesomeIcon icon={faChevronLeft} />
            </i>
          </button>
          <button onClick={scrollNext}>
            <i>
              <FontAwesomeIcon icon={faChevronRight} />
            </i>
          </button>
        </div>
      </div>

      {resp.loaded && projects.length > 0 && (
        <div className="embla" ref={emblaRef}>
          <div className="embla__container">
            {projects.map((p: any) => (
              <div className="embla__slide" key={p.id}>
                <CarouselItem
                  title={p.company}
                  explode={p.explode}
                  bomVariant={p.bomVariant}
                  bomVariantId={p.bomVariantId}
                  quantity={p.qty}
                  dateCreated={p.created}
                  previewUrl={'/project/'}
                  detailUrl={`/project/${p.id}/`}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {resp.loaded && projects.length === 0 && (
        <div className="no-issues">
          <div role="img" aria-label="celebrate">
            🛠
          </div>
          No Active Projects!
        </div>
      )}

      {!resp.loaded && <CardLoading text={resp.placeholder} error={resp.error} />}
    </ProjectStyled>
  )
}

export default SlidingInfoCard
